import mitt from 'mitt';

import type { EventMsg, TEvent } from '~shared/services/eventsInbox';
import { parseWsEvent } from '~shared/services/ws/parse';

const POLLING_INTERVAL = 5000;

class EventsPoller {
	private sync: number | undefined;
	private emitter = mitt<{ event: EventMsg }>();
	private fetching = false;
	private timer: ReturnType<typeof setTimeout> | undefined;
	private pollingFn: ((sync?: string) => Promise<TEvent[]>) | null = null;

	onMsg(cb: (msg: EventMsg) => void) {
		this.emitter.on('event', cb);
		return () => {
			this.emitter.off('event', cb);
		};
	}

	start = (pollingFn: (sync?: string) => Promise<TEvent[]>) => {
		this.pollingFn = pollingFn;
		this.timer && clearTimeout(this.timer);
		this.timer = undefined;
		this.pollEvent();
	};

	stop = () => {
		this.timer && clearTimeout(this.timer);
		this.timer = undefined;
	};

	pollEvent = () => {
		if (this.fetching) {
			return;
		}
		if (!this.pollingFn) {
			return;
		}
		this.fetching = true;

		const params: { sync?: string } = {};
		if (this.sync) {
			params.sync = this.sync.toString();
		}

		void this.pollingFn(this.sync?.toString())
			.then((response) => {
				response.forEach((data) => {
					const event = parseWsEvent(data);
					const newSync = +data.sync;
					if (newSync > (this.sync || 0)) {
						this.sync = newSync;
					}
					this.emitter.emit('event', event);
				});
			})
			.finally(() => {
				this.fetching = false;
				this.timer = setTimeout(() => {
					this.pollEvent();
				}, POLLING_INTERVAL);
			});
	};
}

export const eventsPoller = new EventsPoller();
